import { useEffect, useRef } from 'react';

import { isUsableTimeZone, nextUtcOffsetChange } from 'config/time';

/** setTimeout に渡せる上限（約24.8日）より短い待ち。これより先の切り替わりは、上限で起きて置き直す。 */
export const MAX_TIMER_MILLIS = 24 * 24 * 60 * 60 * 1000;

/**
 * 時間帯の UTC との差が次に切り替わる時刻（夏時間に入る・抜ける）に onChange を呼ぶ。
 * RV-280・RV-281・RV-284（PR #216 の自動レビュー）。
 *
 * 次の切り替わりが上限より先なら、上限で起きて次のタイマーを必ず置き直す（依存の変化に頼らない）。
 * 以前、会社の設定の画面は上限で起きたときに読み直すだけで、読み直しが同じ値を返すと effect が
 * 再び走らず、実際の切り替わりを見るタイマーが無くなっていた。
 * ブラウザが知らない時間帯（切り替わりを求められない）では何もしない。
 */
export function useUtcOffsetChange(zone: string | null | undefined, onChange: () => void): void {
  const callback = useRef(onChange);
  callback.current = onChange;

  useEffect(() => {
    if (!zone || !isUsableTimeZone(zone)) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      clearTimeout(timer);
      const now = Date.now();
      const next = nextUtcOffsetChange(new Date(now), zone);
      const wait = next ? Math.min(next.getTime() - now + 1000, MAX_TIMER_MILLIS) : MAX_TIMER_MILLIS;
      timer = setTimeout(() => {
        // 上限で起きたとき（まだ切り替わっていない）は呼ばずに置き直すだけ。
        if (next && Date.now() >= next.getTime()) callback.current();
        schedule();
      }, Math.max(1000, wait));
    };
    schedule();
    return () => clearTimeout(timer);
  }, [zone]);
}
